import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { api } from "./api";
import type { Me } from "./types";

export const meQueryKey = ["me"] as const;

export function useMeQuery() {
  return useQuery({ queryKey: meQueryKey, queryFn: () => api.get<Me>("/api/v1/me"), retry: false, staleTime: 30_000 });
}

const MeContext = createContext<Me | null>(null);

export function MeProvider({ me, children }: { me: Me; children: ReactNode }) {
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

export function useMe(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error("useMe must be used inside an authenticated route");
  return me;
}

/** UI hint only — every action is re-authorised by the server. */
export function useCan() {
  const me = useMe();
  return (permission: string) => me.permissions.includes(permission);
}

export function useResetSession() {
  const qc = useQueryClient();
  return () => qc.clear();
}
